/**
 * Helpers for extracting legacy inline chart payloads from chat markdown.
 *
 * Some model responses embed chart configs as Markdown image URLs:
 * `![Title](data-chart:{...})`. The chat renderer converts those payloads
 * into first-class chart components before markdown rendering so users never
 * see raw JSON.
 *
 * @module components/chat/messages/chart-markdown
 */

import { normalizeChartSpec, type ChartSpec } from "@taqat/core/types";

export type ChartMarkdownSegment =
  | { type: "markdown"; text: string }
  | { type: "chart"; alt: string; config: ChartSpec };

const CHART_IMAGE_PREFIX = "![";
const CHART_URL_PREFIX = "data-chart:";

function findJsonObjectEnd(input: string, start: number): number {
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let index = start; index < input.length; index += 1) {
    const char = input[index];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === "\"") {
        inString = false;
      }
      continue;
    }

    if (char === "\"") {
      inString = true;
      continue;
    }

    if (char === "{") {
      depth += 1;
    } else if (char === "}") {
      depth -= 1;
      if (depth === 0) return index;
    }
  }

  return -1;
}

function pushMarkdown(
  segments: ChartMarkdownSegment[],
  text: string,
): void {
  if (!text) return;
  const last = segments[segments.length - 1];
  if (last?.type === "markdown") {
    last.text += text;
    return;
  }
  segments.push({ type: "markdown", text });
}

/**
 * Splits chat markdown into normal text and renderable chart payloads.
 */
export function splitChartMarkdown(input: string): ChartMarkdownSegment[] {
  const segments: ChartMarkdownSegment[] = [];
  let cursor = 0;

  while (cursor < input.length) {
    const imageStart = input.indexOf(CHART_IMAGE_PREFIX, cursor);
    if (imageStart === -1) {
      pushMarkdown(segments, input.slice(cursor));
      break;
    }

    const altEnd = input.indexOf("](", imageStart + CHART_IMAGE_PREFIX.length);
    if (altEnd === -1) {
      pushMarkdown(segments, input.slice(cursor));
      break;
    }

    const urlStart = altEnd + 2;
    if (!input.startsWith(CHART_URL_PREFIX, urlStart)) {
      pushMarkdown(segments, input.slice(cursor, altEnd + 2));
      cursor = altEnd + 2;
      continue;
    }

    const jsonStart = urlStart + CHART_URL_PREFIX.length;
    if (input[jsonStart] !== "{") {
      pushMarkdown(segments, input.slice(cursor, jsonStart));
      cursor = jsonStart;
      continue;
    }

    const jsonEnd = findJsonObjectEnd(input, jsonStart);
    if (jsonEnd === -1) {
      pushMarkdown(segments, input.slice(cursor));
      break;
    }

    const closeParen = jsonEnd + 1;
    if (input[closeParen] !== ")") {
      pushMarkdown(segments, input.slice(cursor, closeParen));
      cursor = closeParen;
      continue;
    }

    pushMarkdown(segments, input.slice(cursor, imageStart));

    const alt = input.slice(imageStart + CHART_IMAGE_PREFIX.length, altEnd);
    const payload = input.slice(jsonStart, jsonEnd + 1);
    try {
      const config = normalizeChartSpec(JSON.parse(payload) as unknown);
      if (config) {
        segments.push({ type: "chart", alt, config });
      } else {
        pushMarkdown(segments, `\n\n> Chart "${alt || "Untitled"}" could not be rendered.\n\n`);
      }
    } catch {
      pushMarkdown(segments, `\n\n> Chart "${alt || "Untitled"}" could not be rendered.\n\n`);
    }

    cursor = closeParen + 1;
  }

  return segments;
}
